import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import Header from './Header';
import { apiService } from '../services/api';
import {
  CatalystCalendarPanel,
  pickMatchingCalendar,
  type CalendarCatalogItem,
} from './CatalystCalendarsView';

type ContentMode = 'landscape' | 'calendar';

const DATA_COL_MIN_CLASS = 'min-w-[160px] w-[160px]';

export type LandscapeTier = 'soc' | 'niche' | 'emerging';

export interface LandscapeLinkedTrial {
  nct_id: string;
  trial_id?: number | null;
  trial_name?: string | null;
  overall_status?: string | null;
  phases?: string | null;
  trial_url?: string | null;
  ongoing_trials_url?: string | null;
}

export interface LandscapeColumn {
  id: number;
  sort_order: number;
  display_name: string;
  column_tier: LandscapeTier;
  group_label?: string | null;
  is_soc: boolean;
  phase_tags: string[];
  phase_label?: string | null;
  nct_ids: string[];
  is_visible_default: boolean;
  linked_trials: LandscapeLinkedTrial[];
}

export interface LandscapeRow {
  id: number;
  sort_order: number;
  row_key: string;
  label: string;
  section: string;
  value_kind: string;
}

export interface LandscapeNote {
  section_key: string;
  title?: string | null;
  body?: string | null;
  sort_order: number;
}

export interface LandscapeMeta {
  id: number;
  slug: string;
  title: string;
  subtitle?: string | null;
  disease_area?: string | null;
  line_of_therapy?: string | null;
  version_label?: string | null;
  version_number?: number;
  is_published?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
  indication_id?: number | null;
  indication_name?: string | null;
}

export interface LandscapeCatalogItem {
  id: number;
  slug: string;
  title: string;
  subtitle?: string | null;
  disease_area?: string | null;
  line_of_therapy?: string | null;
  version_label?: string | null;
  version_number?: number;
  is_published?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
  indication_id?: number | null;
  indication_name?: string | null;
  column_count: number;
}

const PHASE_OPTIONS = [
  { id: '1', label: 'Phase 1' },
  { id: '2', label: 'Phase 2' },
  { id: '3', label: 'Phase 3' },
] as const;

/** Opaque tier fills; borders match fill so sticky gaps don't show scrolling content. */
const TIER_HEADER: Record<LandscapeTier, string> = {
  soc: 'bg-emerald-800 text-white border-emerald-800',
  niche: 'bg-teal-700 text-white border-teal-700',
  emerging: 'bg-sky-800 text-white border-sky-800',
};

const TIER_FILL: Record<LandscapeTier, string> = {
  soc: '#065f46', // emerald-800
  niche: '#0f766e', // teal-700
  emerging: '#075985', // sky-800
};

function normalizeTier(tier: string): LandscapeTier {
  if (tier === 'soc' || tier === 'niche') return tier;
  return 'emerging';
}

function fallbackGroupLabel(tier: LandscapeTier): string {
  if (tier === 'soc') return 'STANDARD OF CARE';
  if (tier === 'niche') return 'FDA-APPROVED (NICHE)';
  return 'EMERGING THERAPIES';
}

function columnGroupLabel(col: LandscapeColumn): string {
  const raw = (col.group_label || '').trim();
  if (raw) return raw;
  return fallbackGroupLabel(normalizeTier(col.column_tier));
}

type HeaderGroupSpan = {
  key: string;
  label: string;
  tier: LandscapeTier;
  colSpan: number;
};

/** Build contiguous group spans from currently visible columns (CSV labels preserved). */
function buildHeaderGroupSpans(columns: LandscapeColumn[]): HeaderGroupSpan[] {
  const spans: HeaderGroupSpan[] = [];
  for (const col of columns) {
    const tier = normalizeTier(col.column_tier);
    const label = columnGroupLabel(col);
    const last = spans[spans.length - 1];
    if (last && last.label === label && last.tier === tier) {
      last.colSpan += 1;
    } else {
      spans.push({
        key: `${col.id}-${label}`,
        label,
        tier,
        colSpan: 1,
      });
    }
  }
  return spans;
}

function cellText(cells: Record<string, string | null | undefined>, columnId: number, rowId: number): string {
  const v = cells[`${columnId}:${rowId}`];
  if (v == null || String(v).trim() === '') return '—';
  return String(v);
}

/** Raw cell value without the empty placeholder (for parsing metadata). */
function rawCellText(
  cells: Record<string, string | null | undefined>,
  columnId: number,
  rowId: number,
): string {
  const v = cells[`${columnId}:${rowId}`];
  if (v == null) return '';
  return String(v).trim();
}

/** Parse Phase 1/2/3 tags from Clinical Stage (or similar) cell text. */
function parsePhaseTagsFromText(text?: string | null): string[] {
  const lower = (text || '').toLowerCase();
  if (!lower.trim()) return [];
  const tags: string[] = [];
  if (/\bph(?:ase)?\s*3\b|\bph3\b|\bpivotal\b|fda-approved|\bapproved\b/.test(lower)) {
    tags.push('3');
  }
  if (/\bph(?:ase)?\s*2\b|\bph2\b/.test(lower)) {
    tags.push('2');
  }
  if (/\bph(?:ase)?\s*1(?:\s*\/\s*2)?(?:[ab])?\b|\bph1(?:[ab])?\b/.test(lower)) {
    tags.push('1');
  }
  return tags;
}

function isClinicalStageRow(row: LandscapeRow): boolean {
  const label = (row.label || '').toLowerCase();
  const key = (row.row_key || '').toLowerCase();
  return (
    label === 'stage' ||
    label.startsWith('stage ') ||
    label.includes('clinical stage') ||
    label.endsWith(' stage') ||
    key.includes('clinical-stage') ||
    key === 'stage' ||
    (label.includes('stage') && !label.includes('disease stage'))
  );
}

function effectivePhaseTags(
  col: LandscapeColumn,
  stageRow: LandscapeRow | null,
  cells: Record<string, string | null | undefined>,
): string[] {
  const stored = (col.phase_tags || []).map(String).filter(Boolean);
  const numericStored = stored.filter((t) => t === '1' || t === '2' || t === '3');
  if (numericStored.length > 0) return numericStored;
  if (!stageRow) return [];
  return parsePhaseTagsFromText(rawCellText(cells, col.id, stageRow.id));
}

function formatUploadedAt(value?: string | null): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function versionSummary(item: {
  version_label?: string | null;
  version_number?: number;
  created_at?: string | null;
}): string {
  const parts: string[] = [];
  if (item.version_label) parts.push(item.version_label);
  if (item.version_number != null) parts.push(`v${item.version_number}`);
  const uploaded = formatUploadedAt(item.created_at);
  if (uploaded) parts.push(`uploaded ${uploaded}`);
  return parts.join(' · ');
}

function diseaseAreaKey(value?: string | null): string {
  return (value || '').trim().toLowerCase().replace(/\s+/g, '-');
}

function diseaseAreaPath(value?: string | null): string {
  const key = diseaseAreaKey(value);
  return key ? `/emerging-landscapes/${encodeURIComponent(key)}` : '/emerging-landscapes';
}

/**
 * Normalize a landscape line label into a subtype key for grouping
 * (e.g. "1L ALK+ NSCLC" and "Adjuvant ALK+ NSCLC" → "alk+ nsclc").
 */
function lineSubtypeKey(line?: string | null, indication?: string | null, title?: string | null): string {
  let label = lineLabel(line, indication, title).trim();
  if (!label) return '';
  // Drop leading line / setting tokens so related indications cluster.
  label = label
    .replace(
      /^(neoadjuvant(?:\s*\/\s*perioperative)?|adjuvant(?:\s*\/?\s*perioperative)?|perioperative|consolidative|consolidation|preservation|induction|maintenance|maint)\s+/i,
      '',
    )
    .replace(/^\d+\s*L\s*\+?\s*(?:maintenance|maint|induction)?\s*/i, '')
    .replace(/^(first[-\s]?line|second[-\s]?line|third[-\s]?line)\s+/i, '')
    .replace(/\s+/g, ' ')
    .trim();
  return label.toLowerCase();
}

/**
 * Sort key for treatment setting / line within a subtype group.
 * Order: adjuvant / neoadjuvant → 1L → 2L → later lines → other.
 */
function lineStageSortKey(line?: string | null, indication?: string | null, title?: string | null): number {
  const label = lineLabel(line, indication, title).toUpperCase().replace(/\s+/g, ' ');
  if (!label) return 900;

  if (/\bNEOADJUVANT\b/.test(label) || /\bPERIOPERATIVE\b/.test(label)) return 5;
  if (/\bADJUVANT\b/.test(label)) return 10;
  if (/\bPRESERVATION\b|\bTRIMODALITY\b/.test(label)) return 15;
  if (/\bCONSOLIDAT/.test(label)) return 18;
  if (/\bINDUCTION\b/.test(label) && !/\d\s*L/.test(label)) return 20;

  const match = label.match(/(?:^|[\s—\-–])(\d+)\s*L\s*(\+)?/i) || label.match(/^(\d+)\s*L\s*(\+)?/i);
  if (match) {
    let n = Number(match[1]) * 100;
    if (/MAINTENANCE|MAINT\b/.test(label)) n += 20;
    if (match[2] || label.includes('+')) n += 50;
    return 100 + n;
  }

  if (/\bPLATINUM[-\s]?RESISTANT\b|\bPROC\b/.test(label)) return 700;
  if (/\bPLATINUM[-\s]?SENSITIVE\b|\bPSR\b/.test(label)) return 650;
  return 800;
}

/** Compare two landscape catalog items: subtype groups, then adjuvant→1L→2L. */
function compareLandscapeLines(
  a: { line_of_therapy?: string | null; indication_name?: string | null; title?: string | null },
  b: { line_of_therapy?: string | null; indication_name?: string | null; title?: string | null },
): number {
  const subtypeA = lineSubtypeKey(a.line_of_therapy, a.indication_name, a.title);
  const subtypeB = lineSubtypeKey(b.line_of_therapy, b.indication_name, b.title);
  const bySubtype = subtypeA.localeCompare(subtypeB);
  if (bySubtype !== 0) return bySubtype;

  const byStage =
    lineStageSortKey(a.line_of_therapy, a.indication_name, a.title) -
    lineStageSortKey(b.line_of_therapy, b.indication_name, b.title);
  if (byStage !== 0) return byStage;

  return lineLabel(a.line_of_therapy, a.indication_name, a.title).localeCompare(
    lineLabel(b.line_of_therapy, b.indication_name, b.title),
  );
}

function isBareLineToken(text: string): boolean {
  return /^\d+\s*L\s*\+?$/i.test(text.trim());
}

function formatBareLineToken(text: string): string {
  const match = text.trim().match(/^(\d+)\s*L\s*(\+)?/i);
  if (!match) return text.trim();
  return `${match[1]}L${match[2] || ''}`;
}

/**
 * Build a display phrase from indication / title / line text.
 * Examples: "2L+ PDAC" → "2L+", "1L ES-SCLC (maintenance)" → "1L maintenance",
 * "SCLC — 1L ES-SCLC (maintenance) Landscape" → "1L maintenance"
 */
function extractLinePhrase(text?: string | null): string | null {
  if (!text) return null;
  const trimmed = text.trim();
  const match =
    trimmed.match(/^(\d+)\s*L\s*(\+)?/i) ||
    trimmed.match(/(?:^|[\s—\-–—,:(]+)(\d+)\s*L\s*(\+)?/i);
  if (!match) return null;
  const base = `${match[1]}L${match[2] || ''}`;
  // Use the slice from the line token onward for qualifier detection
  const fromToken = trimmed.slice(Math.max(0, trimmed.search(/\d+\s*L\s*\+?/i)));
  const rest = fromToken.replace(/^\d+\s*L\s*\+?/i, '');
  if (/\bmaintenance\b|\bmaint\b/i.test(rest) || /\(\s*maintenance\s*\)/i.test(trimmed)) {
    return `${base} maintenance`;
  }
  const qualifier = rest.match(
    /^\s+(maintenance|maint|induction|adjuvant|neoadjuvant|consolidative|consolidation)\b/i,
  );
  if (qualifier) {
    const word = qualifier[1].toLowerCase();
    return `${base} ${word === 'maint' ? 'maintenance' : word}`;
  }
  return base;
}

/**
 * Line toggle / catalog label — show the uploaded line text exactly when present.
 */
function lineLabel(
  line?: string | null,
  indication?: string | null,
  title?: string | null,
): string {
  const lineTrim = (line || '').trim().replace(/\s+/g, ' ');
  if (lineTrim) return lineTrim;

  const fromIndication = extractLinePhrase(indication);
  const fromTitle = extractLinePhrase(title);
  const rich = [fromIndication, fromTitle].find((p) => p && !isBareLineToken(p)) || null;
  const plus = [fromIndication, fromTitle].find((p) => p && p.includes('+')) || null;
  return rich || plus || fromIndication || fromTitle || 'Landscape';
}

type DiseaseGroup = {
  key: string;
  label: string;
  items: LandscapeCatalogItem[];
};

function groupByDiseaseArea(items: LandscapeCatalogItem[]): DiseaseGroup[] {
  const map = new Map<string, DiseaseGroup>();
  for (const item of items) {
    const key = diseaseAreaKey(item.disease_area) || item.slug;
    const label = (item.disease_area || item.slug).trim() || item.slug;
    if (!map.has(key)) map.set(key, { key, label, items: [] });
    map.get(key)!.items.push(item);
  }
  for (const group of map.values()) {
    group.items.sort((a, b) => compareLandscapeLines(a, b) || a.title.localeCompare(b.title));
  }
  return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
}

const EmergingLandscapesCatalog: React.FC = () => {
  const [items, setItems] = useState<LandscapeCatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.fetchEmergingLandscapes();
        if (!cancelled) {
          if (response?.success) setItems(response.data || []);
          else setError(response?.error || 'Failed to load landscapes');
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load landscapes');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const groups = useMemo(() => groupByDiseaseArea(items), [items]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans">
      <Header />
      <main className="max-w-5xl mx-auto px-4 py-6">
        <div className="bg-white rounded-xl border border-gray-100 shadow-card px-5 py-4 mb-4">
          <h1 className="text-xl font-bold text-slate-900">Emerging Therapies</h1>
          <p className="text-sm text-slate-500 mt-1">
            Open a disease, pick a line, then switch between landscape, ongoing trials, and catalyst calendar.
          </p>
        </div>
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
          </div>
        ) : error ? (
          <p className="text-rose-600 font-medium">{error}</p>
        ) : groups.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 p-8 text-slate-500 italic">
            No landscapes published yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {groups.map((group) => (
              <Link
                key={group.key}
                to={diseaseAreaPath(group.label)}
                className="bg-white rounded-xl border border-slate-200 px-4 py-3 hover:border-indigo-300 hover:bg-indigo-50/40 transition-colors"
              >
                <div className="text-sm font-bold text-slate-900">{group.label}</div>
                <div className="text-xs text-slate-500 mt-1">
                  {group.items.map((item) => lineLabel(item.line_of_therapy, item.indication_name, item.title)).join(' · ')}
                  {` · ${group.items.length} landscape${group.items.length === 1 ? '' : 's'}`}
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

const EmergingLandscapeDetail: React.FC = () => {
  const { slug: routeParam } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const viewParam = (searchParams.get('view') || '').toLowerCase();
  const contentMode: ContentMode = viewParam === 'calendar' ? 'calendar' : 'landscape';

  const setContentMode = (mode: ContentMode) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (mode === 'calendar') next.set('view', 'calendar');
        else next.delete('view');
        return next;
      },
      { replace: true },
    );
  };

  const [catalog, setCatalog] = useState<LandscapeCatalogItem[]>([]);
  const [calendarCatalog, setCalendarCatalog] = useState<CalendarCatalogItem[]>([]);
  const [calendarCatalogLoading, setCalendarCatalogLoading] = useState(false);
  const [activeSlug, setActiveSlug] = useState<string>('');
  const [landscape, setLandscape] = useState<LandscapeMeta | null>(null);
  const [columns, setColumns] = useState<LandscapeColumn[]>([]);
  const [rows, setRows] = useState<LandscapeRow[]>([]);
  const [cells, setCells] = useState<Record<string, string | null | undefined>>({});
  const [notes, setNotes] = useState<LandscapeNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showSoc, setShowSoc] = useState(true);
  const [phaseFilters, setPhaseFilters] = useState<string[]>(['1', '2', '3']);
  const [hiddenColumnIds, setHiddenColumnIds] = useState<Set<number>>(new Set());
  const [expandedCells, setExpandedCells] = useState<Set<string>>(new Set());
  const [showNotes, setShowNotes] = useState(false);
  /** Column visibility chips are collapsed by default to free vertical space for the matrix. */
  const [showColumnChips, setShowColumnChips] = useState(false);
  /** Sections start expanded; user can collapse Design / Efficacy / Safety blocks. */
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const theadRef = useRef<HTMLTableSectionElement | null>(null);
  const [theadHeight, setTheadHeight] = useState(0);

  // Resolve disease-area family + default slug from route param (disease key or legacy slug).
  useEffect(() => {
    if (!routeParam) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const listRes = await apiService.fetchEmergingLandscapes();
        if (cancelled) return;
        if (!listRes?.success) {
          setError(listRes?.error || 'Failed to load landscapes');
          setLoading(false);
          return;
        }
        const items: LandscapeCatalogItem[] = listRes.data || [];
        setCatalog(items);

        const bySlug = items.find((item) => item.slug === routeParam);
        const diseaseKey = bySlug
          ? diseaseAreaKey(bySlug.disease_area) || bySlug.slug
          : diseaseAreaKey(routeParam) || routeParam.toLowerCase();

        const family = items
          .filter((item) => (diseaseAreaKey(item.disease_area) || item.slug) === diseaseKey)
          .sort(compareLandscapeLines);

        if (family.length === 0) {
          setError('No landscapes found for this disease area.');
          setLoading(false);
          return;
        }

        const preferred = bySlug?.slug || family[0].slug;
        setActiveSlug(preferred);
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load landscapes');
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [routeParam]);

  // Load calendar catalog only when the Calendar tab is opened.
  useEffect(() => {
    if (contentMode !== 'calendar' || calendarCatalog.length > 0) return;
    let cancelled = false;
    setCalendarCatalogLoading(true);
    (async () => {
      try {
        const calRes = await apiService.fetchEmergingCalendars();
        if (!cancelled && calRes?.success) {
          setCalendarCatalog(calRes.data || []);
        }
      } catch {
        // Calendar catalog is optional.
      } finally {
        if (!cancelled) setCalendarCatalogLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [contentMode, calendarCatalog.length]);

  // Load matrix for the active line/slug (skip heavy matrix fetch in Calendar-only mode).
  useEffect(() => {
    if (!activeSlug) return;
    if (contentMode === 'calendar') {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.fetchEmergingLandscape(activeSlug);
        if (cancelled) return;
        if (!response?.success) {
          setError(response?.error || 'Failed to load landscape');
          return;
        }
        setLandscape(response.landscape);
        setColumns(response.columns || []);
        // Preserve CSV / DB order (sort_order), no section regrouping.
        const orderedRows = [...(response.rows || [])].sort(
          (a: LandscapeRow, b: LandscapeRow) => a.sort_order - b.sort_order || a.id - b.id,
        );
        setRows(orderedRows);
        setCells(response.cells || {});
        setNotes(response.notes || []);
        setExpandedCells(new Set());
        setCollapsedSections(new Set());
        const defaultsHidden = new Set<number>(
          (response.columns || [])
            .filter((c: LandscapeColumn) => c.is_visible_default === false)
            .map((c: LandscapeColumn) => c.id),
        );
        setHiddenColumnIds(defaultsHidden);
      } catch (err: unknown) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load landscape');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeSlug, contentMode]);

  const lineOptions = useMemo(() => {
    if (!landscape && !catalog.length) return [];
    const diseaseKey =
      diseaseAreaKey(landscape?.disease_area) ||
      diseaseAreaKey(catalog.find((c) => c.slug === activeSlug)?.disease_area) ||
      diseaseAreaKey(routeParam);
    return catalog
      .filter((item) => (diseaseAreaKey(item.disease_area) || item.slug) === diseaseKey)
      .sort(compareLandscapeLines);
  }, [catalog, landscape, activeSlug, routeParam]);

  const diseaseTitle = useMemo(() => {
    const area = landscape?.disease_area || lineOptions[0]?.disease_area || routeParam || 'Landscape';
    return area;
  }, [landscape, lineOptions, routeParam]);

  const activeLineMeta = useMemo(
    () => lineOptions.find((o) => o.slug === activeSlug) || landscape,
    [lineOptions, activeSlug, landscape],
  );

  const trialTrackerIndicationId = useMemo(() => {
    const indicationId =
      landscape?.indication_id ??
      (activeLineMeta && 'indication_id' in activeLineMeta ? activeLineMeta.indication_id : null);
    return indicationId != null && Number.isFinite(Number(indicationId))
      ? String(indicationId)
      : null;
  }, [landscape, activeLineMeta]);

  const openOngoingTrials = () => {
    const url = trialTrackerIndicationId
      ? `/trial-tracker?indication=${encodeURIComponent(trialTrackerIndicationId)}`
      : '/trial-tracker';
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const matchedCalendar = useMemo(() => {
    if (!calendarCatalog.length) return null;
    return pickMatchingCalendar(calendarCatalog, {
      disease_area: landscape?.disease_area || diseaseTitle,
      line_of_therapy:
        landscape?.line_of_therapy ||
        (activeLineMeta && 'line_of_therapy' in activeLineMeta
          ? activeLineMeta.line_of_therapy
          : null),
      indication_id: landscape?.indication_id ?? null,
      indication_name:
        landscape?.indication_name ||
        (activeLineMeta && 'indication_name' in activeLineMeta
          ? activeLineMeta.indication_name
          : null),
      title: landscape?.title || null,
    });
  }, [calendarCatalog, landscape, activeLineMeta, diseaseTitle]);

  const clinicalStageRow = useMemo(
    () => rows.find((row) => isClinicalStageRow(row)) || null,
    [rows],
  );

  const visibleColumns = useMemo(() => {
    return columns.filter((col) => {
      if (hiddenColumnIds.has(col.id)) return false;
      // Show SoC is independent of Phase filters — otherwise unchecking Ph3/Approved
      // prevents SoC columns from coming back when Show SoC is re-enabled.
      if (col.is_soc) return showSoc;
      if (phaseFilters.length === 0) return false;
      // Prefer stored phase_tags; if missing (older uploads / "Clinical Stage" row
      // not detected at import), parse from the Clinical Stage cell.
      const numericPhases = effectivePhaseTags(col, clinicalStageRow, cells);
      if (numericPhases.length === 0) return true;
      return numericPhases.some((t) => phaseFilters.includes(t));
    });
  }, [columns, hiddenColumnIds, showSoc, phaseFilters, clinicalStageRow, cells]);

  const headerGroupSpans = useMemo(
    () => buildHeaderGroupSpans(visibleColumns),
    [visibleColumns],
  );

  const rowSections = useMemo(() => {
    const SECTION_TITLES: Record<string, string> = {
      design: 'Design',
      identity: 'Design',
      efficacy: 'Efficacy',
      safety: 'Safety',
      sources: 'Sources',
    };
    const normalize = (raw?: string | null) => {
      const key = (raw || 'design').trim().toLowerCase() || 'design';
      return key === 'identity' ? 'design' : key;
    };
    const groups: Array<{ key: string; title: string; rows: LandscapeRow[] }> = [];
    for (const row of rows) {
      const key = normalize(row.section);
      const last = groups[groups.length - 1];
      if (last && last.key === key) {
        last.rows.push(row);
      } else {
        groups.push({
          key,
          title: SECTION_TITLES[key] || key.replace(/_/g, ' '),
          rows: [row],
        });
      }
    }
    return groups;
  }, [rows]);

  const togglePhase = (id: string) => {
    setPhaseFilters((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  };

  const toggleColumnHidden = (id: number) => {
    setHiddenColumnIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleCell = (key: string) => {
    setExpandedCells((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSection = (sectionKey: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionKey)) next.delete(sectionKey);
      else next.add(sectionKey);
      return next;
    });
  };

  const labelWidth = 'w-[140px] min-w-[140px] max-w-[140px]';
  const isCalendar = contentMode === 'calendar';
  const isLandscape = contentMode === 'landscape';

  useEffect(() => {
    if (!isLandscape || loading) return;
    const el = theadRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const update = () => setTheadHeight(Math.ceil(el.getBoundingClientRect().height));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isLandscape, loading, visibleColumns.length, headerGroupSpans.length, activeSlug]);

  return (
    <div
      className={`${
        isLandscape ? 'h-screen overflow-hidden' : 'min-h-screen'
      } flex flex-col bg-slate-50 text-slate-900 font-sans`}
    >
      <Header />
      <main className="flex-1 min-h-0 flex flex-col px-2 sm:px-3 py-1.5 max-w-[100%] mx-auto w-full">
        <div className="flex-shrink-0 bg-white rounded-lg border border-gray-100 shadow-card px-2.5 py-1.5 mb-1.5 space-y-1">
          {/* Title + mode toggle on one compact row */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <button
                type="button"
                onClick={() => navigate('/emerging-landscapes')}
                className="text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 whitespace-nowrap"
              >
                ← All
              </button>
              <h1 className="text-sm font-bold text-slate-900 leading-none truncate">{diseaseTitle}</h1>
              {isLandscape && landscape && (
                <span className="hidden md:inline text-[10px] text-slate-400 truncate">
                  {versionSummary(landscape)}
                </span>
              )}
              {isCalendar && matchedCalendar && (
                <span className="hidden md:inline text-[10px] text-slate-400 truncate">
                  {matchedCalendar.title}
                  {matchedCalendar.version_label ? ` · ${matchedCalendar.version_label}` : ''}
                </span>
              )}
            </div>

            <div className="inline-flex items-center gap-0.5 bg-slate-50 rounded-lg border border-slate-200 p-0.5">
              {(
                [
                  ['landscape', 'Landscape'],
                  ['calendar', 'Calendar'],
                ] as const
              ).map(([mode, label]) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setContentMode(mode)}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all ${
                    contentMode === mode
                      ? 'bg-indigo-600 text-white'
                      : 'text-slate-600 hover:bg-white'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={openOngoingTrials}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold border border-slate-200 text-slate-600 hover:border-indigo-300 hover:text-indigo-700"
            >
              Ongoing Trials
              <ExternalLink size={12} className="opacity-70" />
            </button>
          </div>

          {lineOptions.length > 0 && (
            <div className="flex flex-wrap items-center gap-1">
              <span className="text-[9px] uppercase tracking-wide text-slate-400 font-bold mr-0.5">
                Line
              </span>
              {lineOptions.map((opt) => (
                <button
                  key={opt.slug}
                  type="button"
                  onClick={() => setActiveSlug(opt.slug)}
                  className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
                    activeSlug === opt.slug
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
                  }`}
                >
                  {lineLabel(opt.line_of_therapy, opt.indication_name, opt.title)}
                </button>
              ))}
            </div>
          )}

          {isLandscape && (
            <div className="flex flex-wrap items-center gap-1">
              <label className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 border border-slate-200 rounded-full px-2 py-0.5">
                <input type="checkbox" checked={showSoc} onChange={(e) => setShowSoc(e.target.checked)} />
                SoC
              </label>
              <span className="text-[9px] uppercase tracking-wide text-slate-400 font-bold ml-0.5">
                Phase
              </span>
              {PHASE_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => togglePhase(opt.id)}
                  className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
                    phaseFilters.includes(opt.id)
                      ? 'bg-slate-800 text-white border-slate-800'
                      : 'bg-white text-slate-600 border-slate-200'
                  }`}
                >
                  {opt.label.replace('Phase ', 'Ph')}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setShowColumnChips((v) => !v)}
                className={`ml-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
                  showColumnChips
                    ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                    : 'bg-white text-slate-600 border-slate-200'
                }`}
              >
                Columns{showColumnChips ? ' ▾' : ' ▸'}
              </button>
              {showColumnChips &&
                columns.map((col) => {
                  const hidden = hiddenColumnIds.has(col.id) || (!showSoc && col.is_soc);
                  return (
                    <button
                      key={col.id}
                      type="button"
                      onClick={() => toggleColumnHidden(col.id)}
                      className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border max-w-[140px] truncate ${
                        hidden
                          ? 'bg-slate-50 text-slate-400 border-slate-200 line-through'
                          : 'bg-white text-slate-700 border-slate-300'
                      }`}
                      title={col.display_name}
                    >
                      {col.display_name}
                    </button>
                  );
                })}
            </div>
          )}
        </div>

        {isCalendar ? (
          <div className="flex-1 min-h-0 pb-4">
            {calendarCatalogLoading ? (
              <div className="flex items-center justify-center bg-white rounded-xl border border-gray-100 min-h-[200px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
              </div>
            ) : (
              <CatalystCalendarPanel
                embedded
                slug={matchedCalendar?.slug || null}
                emptyMessage="No catalyst calendar uploaded for this disease line yet."
              />
            )}
          </div>
        ) : (
          <>
            {showNotes && notes.length > 0 && (
              <div className="flex-shrink-0 mb-1.5 bg-white rounded-lg border border-gray-100 px-3 py-2 max-h-28 overflow-auto text-[11px] text-slate-600 space-y-0.5">
                {notes.map((note, idx) => (
                  <div key={`${note.section_key}-${idx}`}>
                    {note.title ? <span className="font-semibold text-slate-800">{note.title} </span> : null}
                    {note.body || ''}
                  </div>
                ))}
              </div>
            )}

            {loading ? (
              <div className="flex-1 flex items-center justify-center bg-white rounded-xl border border-gray-100">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
              </div>
            ) : error ? (
              <div className="flex-1 flex items-center justify-center bg-white rounded-xl border border-gray-100 p-8">
                <p className="text-rose-600 font-medium">{error}</p>
              </div>
            ) : visibleColumns.length === 0 ? (
              <div className="flex-1 flex items-center justify-center bg-white rounded-xl border border-gray-100 p-8">
                <p className="text-slate-500 italic">
                  No columns match the current filters. Adjust Phase / SoC / Columns.
                </p>
              </div>
            ) : (
              <div className="flex-1 min-h-0 bg-white rounded-lg border border-gray-100 shadow-card overflow-x-auto overflow-y-auto">
                <table className="border-separate border-spacing-0 text-[10px] leading-tight min-w-max w-max">
                  <thead
                    ref={theadRef}
                    className="sticky top-0 z-20 bg-slate-800 shadow-[0_1px_0_0_rgb(30_41_59)]"
                  >
                    <tr>
                      <th
                        className={`${labelWidth} bg-slate-800 border border-slate-800 p-1 text-left sticky left-0 z-30`}
                      />
                      {headerGroupSpans.map((span) => (
                        <th
                          key={span.key}
                          colSpan={span.colSpan}
                          className={`border p-0.5 text-center font-bold uppercase tracking-wide ${TIER_HEADER[span.tier]}`}
                          style={{ backgroundColor: TIER_FILL[span.tier], borderColor: TIER_FILL[span.tier] }}
                        >
                          {span.label}
                        </th>
                      ))}
                    </tr>
                    <tr>
                      <th
                        className={`${labelWidth} bg-slate-800 border border-slate-800 border-t-slate-700 p-1 text-left text-slate-300 sticky left-0 z-30`}
                      >
                        Attribute
                      </th>
                      {visibleColumns.map((col) => {
                        const tier = normalizeTier(col.column_tier);
                        return (
                          <th
                            key={`name-${col.id}`}
                            className={`border p-1 align-top text-center font-bold ${DATA_COL_MIN_CLASS} ${TIER_HEADER[tier]}`}
                            style={{ backgroundColor: TIER_FILL[tier], borderColor: TIER_FILL[tier] }}
                          >
                            <div className="whitespace-normal break-words">{col.display_name}</div>
                            {col.linked_trials?.length > 0 && (
                              <div className="mt-0.5 flex flex-wrap justify-center gap-0.5 font-normal normal-case tracking-normal">
                                {col.linked_trials.map((t) =>
                                  t.trial_id ? (
                                    <Link
                                      key={t.nct_id}
                                      to={`/trials/${t.trial_id}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-white/20 hover:bg-white/30"
                                      title={t.trial_name || t.nct_id}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {t.nct_id}
                                      <ExternalLink className="w-2.5 h-2.5" />
                                    </Link>
                                  ) : (
                                    <a
                                      key={t.nct_id}
                                      href={`https://clinicaltrials.gov/study/${t.nct_id}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-white/10 hover:bg-white/20"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {t.nct_id}
                                      <ExternalLink className="w-2.5 h-2.5" />
                                    </a>
                                  ),
                                )}
                              </div>
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {rowSections.map((section) => {
                      const collapsed = collapsedSections.has(section.key);
                      const colSpan = Math.max(1, visibleColumns.length + 1);
                      return (
                        <React.Fragment key={`section-${section.key}`}>
                          <tr>
                            <th
                              colSpan={colSpan}
                              className="sticky z-[15] border border-slate-200 p-0 text-left bg-slate-100 shadow-[0_1px_0_0_rgb(226_232_240)]"
                              style={{ top: theadHeight }}
                            >
                              <button
                                type="button"
                                onClick={() => toggleSection(section.key)}
                                className="w-full flex items-center gap-1.5 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-slate-700 bg-slate-100 hover:bg-slate-200/70"
                                aria-expanded={!collapsed}
                              >
                                {collapsed ? (
                                  <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" />
                                ) : (
                                  <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" />
                                )}
                                <span>{section.title}</span>
                                <span className="ml-1 font-medium normal-case tracking-normal text-slate-400">
                                  ({section.rows.length})
                                </span>
                              </button>
                            </th>
                          </tr>
                          {!collapsed &&
                            section.rows.map((row) => (
                              <tr key={row.id} className="align-top">
                                <th
                                  className={`${labelWidth} sticky left-0 z-10 bg-slate-50 border border-slate-200 p-1 text-left font-semibold text-slate-700`}
                                >
                                  {row.label}
                                </th>
                                {visibleColumns.map((col) => {
                                  const key = `${col.id}:${row.id}`;
                                  const text = cellText(cells, col.id, row.id);
                                  const expanded = expandedCells.has(key);
                                  const long = text.length > 140;
                                  return (
                                    <td
                                      key={key}
                                      className={`border border-slate-200 p-1 text-slate-700 bg-white text-center cursor-default ${DATA_COL_MIN_CLASS}`}
                                      onClick={() => long && toggleCell(key)}
                                      title={long && !expanded ? 'Click to expand' : undefined}
                                    >
                                      <div
                                        className={
                                          expanded || !long
                                            ? 'whitespace-pre-wrap break-words text-center'
                                            : 'line-clamp-2 whitespace-pre-wrap break-words text-center'
                                        }
                                      >
                                        {text}
                                      </div>
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

const EmergingLandscapesView: React.FC = () => {
  const { slug } = useParams<{ slug?: string }>();
  if (slug) return <EmergingLandscapeDetail />;
  return <EmergingLandscapesCatalog />;
};

export default EmergingLandscapesView;
